/**
 * Header Component - Clean dashboard header with profile dropdown
 */

import { Menu, Search, ArrowLeft, User, Settings, LogOut, ChevronDown } from 'lucide-react'
import NotificationsCenter from '../NotificationsCenter'
import { useState, useRef, useEffect } from 'react'

export default function Header({
  company,
  user,
  profile,
  role,
  onMenu,
  onBack,
  onLogout,
  onProfileClick,
  onNavigate,
  showSearch = false,
  pageTitle,
}) {
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef(null)

  // ✅ Clean role label
  const roleLabel = role === 'admin'
    ? 'Admin'
    : role === 'investor'
    ? 'Investor'
    : role === 'staff'
    ? 'Staff'
    : 'Founder'

  const displayName = profile?.full_name || user?.name || user?.email?.split('@')[0] || 'User'
  const initial = (profile?.full_name || user?.name || user?.email || 'U').charAt(0).toUpperCase()

  // ✅ Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSearch = (e) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      onNavigate?.('search', { query: searchQuery })
      setSearchQuery('')
      setSearchOpen(false)
    }
  }

  const handleBack = () => {
    if (onBack) {
      onBack()
    } else {
      onNavigate?.('dashboard')
    }
  }

  const handleProfileClick = () => {
    setDropdownOpen(false)
    onProfileClick?.()
  }

  const handleLogout = () => {
    setDropdownOpen(false)
    onLogout?.()
  }

  return (
    <header className="h-14 shrink-0 bg-surface border-b border-neutral-200 flex items-center justify-between px-3 sm:px-4 md:px-6">
      {/* ============================================================
          LEFT SIDE
          ============================================================ */}
      <div className="flex items-center gap-2 min-w-0">
        {/* Menu button - mobile */}
        <button
          onClick={onMenu}
          className="md:hidden text-brand p-1 -ml-1 focus:outline-none focus:ring-2 focus:ring-brand shrink-0"
          aria-label="Open navigation"
        >
          <Menu size={20} />
        </button>

        {/* Back button */}
        <button
          onClick={handleBack}
          className="flex items-center gap-1 text-sm text-muted hover:text-ink transition-colors shrink-0"
          aria-label="Go back"
        >
          <ArrowLeft size={18} />
          <span className="hidden sm:inline">Back</span>
        </button>

        {/* Page title - mobile only */}
        {pageTitle && (
          <span className="font-semibold text-sm text-ink truncate max-w-[120px] sm:hidden">
            {pageTitle}
          </span>
        )}
      </div>

      {/* ============================================================
          RIGHT SIDE
          ============================================================ */}
      <div className="flex items-center gap-2 sm:gap-4 min-w-0">
        {/* Company name - desktop only */}
        {company && (
          <span className="text-sm font-medium text-ink hidden lg:inline-block truncate max-w-[120px] xl:max-w-[200px]">
            {company}
          </span>
        )}

        {/* Search - desktop */}
        {showSearch && (
          <div className="relative hidden lg:block">
            <form onSubmit={handleSearch}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-48 pl-8 pr-3 py-1.5 text-sm bg-neutral-50 border border-neutral-200 rounded-lg focus:outline-none focus:border-brand transition-colors"
              />
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
            </form>
          </div>
        )}

        {/* Search toggle - mobile */}
        {showSearch && (
          <button
            onClick={() => setSearchOpen(!searchOpen)}
            className="lg:hidden p-1.5 text-neutral-400 hover:text-ink shrink-0"
            aria-label="Search"
          >
            <Search size={18} />
          </button>
        )}

        {/* Notifications */}
        <NotificationsCenter onNavigate={onNavigate} />

        {/* ============================================================
            PROFILE DROPDOWN - Professional & Collapsible
            ============================================================ */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-1.5 px-2 py-1.5 border border-neutral-200 bg-neutral-50 hover:bg-neutral-100 hover:border-brand transition-all rounded-lg cursor-pointer"
            aria-label="Profile menu"
          >
            {/* Avatar */}
            <span className="w-7 h-7 bg-brand rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">
              {initial}
            </span>

            {/* Role badge */}
            <span className="text-[10px] font-medium uppercase tracking-wide text-brand border border-brand-soft bg-brand-soft px-2 py-0.5 rounded shrink-0">
              {roleLabel}
            </span>

            {/* Chevron */}
            <ChevronDown
              size={14}
              className={`text-neutral-400 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {/* Dropdown Menu */}
          {dropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-surface border border-neutral-200 rounded-xl shadow-lg z-50 animate-fade-in overflow-hidden">
              {/* User info */}
              <div className="px-4 py-3 border-b border-neutral-200 bg-neutral-50">
                <p className="text-sm font-semibold text-ink">{displayName}</p>
                <p className="text-xs text-muted truncate">{user?.email}</p>
              </div>

              {/* Menu items */}
              <div className="py-1">
                {/* Profile */}
                <button
                  onClick={handleProfileClick}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-ink hover:bg-neutral-50 transition-colors"
                >
                  <User size={16} className="text-neutral-400" />
                  <span>Profile</span>
                </button>

                {/* Settings - Only for Admin/Founder */}
                {(role === 'admin' || role === 'founder') && (
                  <button
                    onClick={() => {
                      setDropdownOpen(false)
                      onNavigate?.('preferences')
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-ink hover:bg-neutral-50 transition-colors"
                  >
                    <Settings size={16} className="text-neutral-400" />
                    <span>Settings</span>
                  </button>
                )}

                {/* Divider */}
                <div className="border-t border-neutral-100 my-1" />

                {/* Logout */}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50 transition-colors"
                >
                  <LogOut size={16} />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile search overlay */}
      {searchOpen && showSearch && (
        <div className="absolute top-14 left-0 right-0 bg-surface border-b border-neutral-200 p-4 animate-fade-in lg:hidden z-50">
          <form onSubmit={handleSearch} className="flex gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              className="flex-1 px-4 py-2.5 border border-neutral-200 rounded-lg focus:outline-none focus:border-brand"
              autoFocus
            />
            <button type="submit" className="btn-primary text-sm px-4 py-2.5">
              Search
            </button>
            <button
              type="button"
              onClick={() => setSearchOpen(false)}
              className="px-3 py-2.5 text-sm text-muted hover:text-ink"
            >
              Cancel
            </button>
          </form>
        </div>
      )}
    </header>
  )
}